import {
  ConnectedPosition,
  Overlay,
  OverlayPositionBuilder,
  OverlayRef,
} from '@angular/cdk/overlay'
import { ComponentPortal } from '@angular/cdk/portal'
import {
  ComponentRef,
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core'
import { TooltipComponent } from '../../components/tooltip/tooltip.component'
import { OverlayPosition } from '../../enums/overlay-position.enum'
import { LoadingImageComponent } from '../../components/loading-image/loading-image.component'
import { isTheme2026 } from '../../helpers/theme-2026'

export enum TooltipMode {
  TEXT,
  IMAGE,
}

@Directive({
  selector: '[wmFrontTooltip]',
  standalone: true,
})
export class TooltipDirective implements OnInit, OnDestroy {
  /** Vertical slack before a text counts as cut: with a tight line height, glyphs can go 1–2px past the line box
   * without any line being hidden (a line-clamped text hides a whole line) */
  private static readonly VERTICAL_OVERFLOW_TOLERANCE_PX = 2

  /** In the 2026 theme a tooltip waits 400ms before showing, so it doesn't flash while the pointer moves (PRENG-7973) */
  private static readonly SHOW_DELAY_2026_MS = 250

  /** The template used to select the actual message to display in the tooltip */
  @Input('wmFrontTooltip') public template = ''

  /** The position as set in a intelligible way i.e TOP, LEFT, BOTTOM, RIGHT */
  @Input() public position: OverlayPosition | `${OverlayPosition}`

  /** The offset of the tooltip */
  @Input() public offset: number

  /** The max width of the tooltip */
  @Input() public maxWidth: number

  /** The max height of the tooltip */
  @Input() public maxHeight: number

  /** Data to be displayed directly in the tooltip if need be */
  @Input() public data?: string[]

  /** If the data to be displayed is only if some info are hidden from the main element */
  @Input() public onlyOnOverflow: boolean = false

  /** If the data to be displayed is only if some info are hidden from the main element */
  @Input() public display: TooltipMode = TooltipMode.TEXT

  /** The connected positon obj to give to the angular overlay position builder */
  public connectedPosition: ConnectedPosition

  protected _overlay = inject(Overlay)

  protected _overlayPositionBuilder = inject(OverlayPositionBuilder)

  protected _elementRef = inject(ElementRef)

  /**
   * Overlay reference, created on the first show and disposed on destroy.
   * Creating it eagerly appended one host + pane to `.cdk-overlay-container`
   * per instance, which were never removed (SUP-1183)
   */
  private _overlayRef?: OverlayRef

  /** The pending show of the 2026 theme's delay */
  private _showTimer?: ReturnType<typeof setTimeout>

  /** On mouse enter we need to attach the tooltip to the dom and bind the
   * inputs values that are used to fill, position and style the tooltip
   */
  @HostListener('mouseenter')
  public mouseenter(): void {
    clearTimeout(this._showTimer)
    if (isTheme2026()) {
      this._showTimer = setTimeout(
        () => this.show(),
        TooltipDirective.SHOW_DELAY_2026_MS
      )
    } else {
      this.show()
    }
  }

  /** On mouse out we need to detch the tooltip to the dom and make sure that the event emitted
   * does not contain mat-icon as a source, so the tooltip does not blink when hovering the content
   * of the icon itself
   */
  @HostListener('mouseleave')
  public mouseout(): void {
    this.hide()
  }

  /** On mouse click, we sometimes delete the parent of the tooltip, because it is removed from the dom,
   * like in the invoice feature on wm-admin, and we need to clear the tooltip from the view, so we need to apply a custom
   * logic that clears it from the dom by just watching if it has an id or not
   * Usage of this would be in the view
   * <wm-front-icon-button [attr.data-detachId]="id" [wmFrontTooltip]="tooltip"><wm-front-icon-button>
   * This syntax will removed the tooltip from the view on click
   */
  @HostListener('click', ['$event'])
  public destroy(e: MouseEvent): void {
    const event = e as any
    const path = event.path || (event.composedPath && event.composedPath())
    const elId = path.find((el: Element) => {
      return el && el.attributes && el.attributes['data-detachId']
    })

    if (elId) {
      this.hide()
    }
  }

  public ngOnInit(): void {
    this.setPosition()
  }

  public ngOnDestroy(): void {
    clearTimeout(this._showTimer)
    this._overlayRef?.dispose()
    this._overlayRef = undefined
  }

  public hide(): void {
    clearTimeout(this._showTimer)
    this._overlayRef?.detach()
  }

  /** Attaches the tooltip to the dom and binds the inputs values that fill, position and style it */
  private show(): void {
    if (
      !this._overlayRef?.hasAttached() &&
      this.template &&
      (!this.onlyOnOverflow || (this.onlyOnOverflow && this.isOverflowing()))
    ) {
      const overlayRef = this.getOrCreateOverlayRef()
      if (this.display === TooltipMode.TEXT) {
        const tooltipRef: ComponentRef<TooltipComponent> = overlayRef.attach(
          new ComponentPortal(TooltipComponent)
        )
        tooltipRef.instance.template = this.template
        tooltipRef.instance.position = this.position as OverlayPosition
        tooltipRef.instance.offset = this.offset
        tooltipRef.instance.maxWidth = this.maxWidth
        tooltipRef.instance.data = this.data
      } else if (this.display === TooltipMode.IMAGE) {
        const tooltipRef: ComponentRef<LoadingImageComponent> =
          overlayRef.attach(new ComponentPortal(LoadingImageComponent))
        tooltipRef.instance.currentUrl = this.template
        tooltipRef.instance.maxWidth = `${this.maxWidth}px`
        tooltipRef.instance.maxHeight = `${this.maxHeight}px`
        tooltipRef.instance.withBorderAndShadow = true
      }
    }
  }

  /**
   * Creates the overlay on first use only, so a tooltip that is never shown
   * leaves nothing in the overlay container
   * @returns {OverlayRef}
   * @private
   */
  private getOrCreateOverlayRef(): OverlayRef {
    if (!this._overlayRef) {
      const positionStrategy = this._overlayPositionBuilder
        .flexibleConnectedTo(this._elementRef)
        .withFlexibleDimensions(false)
        .withPositions([this.connectedPosition])

      this._overlayRef = this._overlay.create({
        positionStrategy: positionStrategy,
      })
    }
    return this._overlayRef
  }

  /**
   * Will calculate if the element is overflowing, horizontally or vertically (line clamped text)
   * @returns {boolean}
   * @private
   */
  private isOverflowing(): boolean {
    const element: HTMLElement = this._elementRef.nativeElement
    return (
      element.offsetWidth < element.scrollWidth ||
      element.offsetHeight + TooltipDirective.VERTICAL_OVERFLOW_TOLERANCE_PX <
        element.scrollHeight
    )
  }

  /** Sets the position of the overlay */
  private setPosition(): void {
    switch (this.position) {
      case OverlayPosition.TOP:
        this.connectedPosition = {
          originX: 'center',
          originY: 'top',
          overlayX: 'center',
          overlayY: 'bottom',
          offsetY: this.offset,
        }
        break
      case OverlayPosition.BOTTOM:
        this.connectedPosition = {
          originX: 'center',
          originY: 'bottom',
          overlayX: 'center',
          overlayY: 'bottom',
          offsetX: this.offset,
        }
        break
      case OverlayPosition.LEFT:
        this.connectedPosition = {
          originX: 'start',
          originY: 'center',
          overlayX: 'start',
          overlayY: 'center',
          offsetX: this.offset,
        }
        break
      case OverlayPosition.RIGHT:
        this.connectedPosition = {
          originX: 'end',
          originY: 'center',
          overlayX: 'end',
          overlayY: 'center',
          offsetX: this.offset,
        }
        break
      default:
        this.connectedPosition = {
          originX: 'center',
          originY: 'top',
          overlayX: 'center',
          overlayY: 'bottom',
          offsetY: this.offset,
        }
    }
  }
}
